import axios from "axios";
import { useMemo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getNotifications } from "../../../app/selector";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Image, Badge } from "react-bootstrap";

import {
  MainContainer, Sidebar, ConversationList, Conversation, Avatar,
  ChatContainer, ConversationHeader, MessageGroup, Message,
  MessageList, MessageInput, TypingIndicator, Status,
} from "@chatscope/chat-ui-kit-react";

import {
  useChat, ChatMessage, MessageContentType, MessageDirection, MessageStatus,
} from "@chatscope/use-chat";
import { BACKEND_URL } from "constants";
import ConfirmationDialog from "../../common/dialog/ConfirmationDialog";
import Notification from "../../common/dialog/Notification";
import { TrashFill, Ban, ShieldLock } from "react-bootstrap-icons";

const profilePicturePlaceholder = require("../../../resources/ProfilePicturePlaceholder.jpg");

const formatLocalTime = (dateInput) => {
  if (!dateInput) return "";
  const localDate = new Date(dateInput);
  if (isNaN(localDate.getTime())) return "";
  return localDate.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export const Chat = ({ user }) => {
  const navigate = useNavigate();
  const { lastSeenUsers, onlineUsers } = useSelector(getNotifications);

  const [showTimestamp, setShowTimestamp] = useState(null);
  const [show, setShow] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [success, setSuccess] = useState(true);
  const [isDesktopView, setIsDesktopView] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktopView(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setShowOriginal = async (photoId) => {
    const photo = await axios.post(`${BACKEND_URL}/photos/by_id`, { photo_ids: [photoId] });
    if (photo?.data) setShow(`data:image/jpeg;base64,${photo.data[0]["photo"]}`);
  };

  const {
    currentMessages, conversations, activeConversation, setActiveConversation,
    sendMessage, getUser, getConversation, currentMessage, setCurrentMessage,
    sendTyping, setCurrentUser,
  } = useChat();

  useEffect(() => { setCurrentUser(user); }, [user, setCurrentUser]);

  useEffect(() => {
    if (activeConversation) {
      const participant = activeConversation.participants.length > 0 ? activeConversation.participants[0] : undefined;
      if (participant) readMessage(participant.id);
    }
  }, [activeConversation, getConversation]);

  const readMessage = (sender_user_id) => {
    axios.post(`${BACKEND_URL}/read-messages`, { sender_user_id }).catch((e) => console.error(e));
  };

  // Memoized metadata extraction for the currently active conversation
  const activeUserData = useMemo(() => {
    if (!activeConversation) return {};
    const participant = activeConversation.participants[0];
    if (!participant) return {};
    const otherUser = getUser(participant.id);
    if (!otherUser) return {};

    const shouldDisplayPhoto = otherUser.avatar?.["thumbnail"] && !(otherUser.data.isBlocked || otherUser.data.isBeingBlocked);

    return {
      id: participant.id,
      username: otherUser.username,
      isAdmin: otherUser.data?.["isAdmin"],
      isBlocked: otherUser.data?.isBlocked,
      isBeingBlocked: otherUser.data?.isBeingBlocked,
      avatarElement: (
        <Avatar
          src={shouldDisplayPhoto ? otherUser.avatar["thumbnail"] : profilePicturePlaceholder}
          title={shouldDisplayPhoto ? "Zoom" : ""}
          style={shouldDisplayPhoto ? { cursor: "pointer" } : {}}
          onClick={() => { if (shouldDisplayPhoto) setShowOriginal(otherUser.avatar["photo_id"]); }}
        />
      )
    };
  }, [activeConversation, getUser]);

  const handleChange = (value) => {
    setCurrentMessage(value);
    if (activeConversation) {
      sendTyping({ conversationId: activeConversation?.id, isTyping: true, userId: user.id, content: value, throttle: true });
    }
  };

  const handleSend = (text) => {
    const message = new ChatMessage({
      id: "", content: text, contentType: MessageContentType.TextHtml,
      senderId: user.id, direction: MessageDirection.Outgoing, status: MessageStatus.Sent,
    });
    if (activeConversation) sendMessage({ message, conversationId: activeConversation.id, senderId: user.id });
  };

  const handleEmptyMessages = (id) => {
    axios.post(BACKEND_URL + "/empty-messages", { other_user_id: id })
      .then((_) => navigate(0)).catch((e) => console.error(e));
  };

  const blockUser = (id) => {
    axios.post(BACKEND_URL + "/block-user", { block_user_id: id })
      .then(() => { setSuccess(true); setShowNotification(true); })
      .catch((error) => { setSuccess(false); setShowNotification(true); console.error("Error fetching users:", error); });
  };

  const getTypingIndicator = useCallback(() => {
    if (activeConversation) {
      const typingUsers = activeConversation.typingUsers;
      if (typingUsers.length > 0) {
        const typingUserId = typingUsers.items[0].userId;
        if (activeConversation.participantExists(typingUserId)) {
          const typingUser = getUser(typingUserId);
          if (typingUser) return <TypingIndicator content={`${typingUser.username} is typing`} />;
        }
      }
    }
    return undefined;
  }, [activeConversation, getUser]);

  return (
    <MainContainer style={{ height: isDesktopView ? "calc(100vh - 40px)" : "75vh", width: "100%", marginBottom: "20px", borderRadius: "8px", overflow: "hidden" }}>
      <ConfirmationDialog
        text="Are you sure you want to block this user?" show={showConfirmation} setShow={setShowConfirmation}
        operation="Yes, block this user" variant="danger" onConfirm={() => blockUser(activeUserData.id)}
      />
      <Notification
        text={success ? "User has been blocked!" : "Something went wrong!"} variant={success ? "success" : "danger"}
        show={showNotification} setShow={setShowNotification} onClose={() => navigate(0)}
      />
      <Modal show={show} onHide={() => setShow(null)} centered size="lg">
        <Modal.Body className="d-flex justify-content-center align-items-center bg-dark p-1 rounded-3">
          <Image src={show} alt="AvatarCustom" className="img-fluid" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
        </Modal.Body>
      </Modal>

      <Sidebar position="left" scrollable style={{ width: isDesktopView ? "320px" : "76px", minWidth: isDesktopView ? "320px" : "76px", maxWidth: isDesktopView ? "320px" : "76px" }}>
        <ConversationList>
          {conversations.map((c) => {
            const [avatar, name, isAdmin, isBlocked, isBeingBlocked, isOnline, lastSeen] = (() => {
              const participant = c.participants.length > 0 ? c.participants[0] : undefined;
              if (participant) {
                const otherUser = getUser(participant.id);
                if (otherUser) {
                  // 1. Check if the target user in the list is an admin
                  const targetIsAdmin = otherUser.data?.["isAdmin"];

                  // 2. Determine if the CURRENT logged-in user is an admin
                  // (Looking up the logged-in user's data object in chatscope storage)
                  const currentUserData = getUser(user.id);
                  const iAmAdmin = currentUserData?.data?.["isAdmin"];

                  // 3. Apply the visibility rule: 
                  // If the target is an admin, hide their online status UNLESS I am also an admin.
                  const canSeeOnlineStatus = !targetIsAdmin || iAmAdmin;

                  const userIsOnline = canSeeOnlineStatus && onlineUsers.includes(participant.id);
                  const userLastSeen = canSeeOnlineStatus && (participant.id in lastSeenUsers)
                    ? lastSeenUsers[participant.id]
                    : undefined;

                  const shouldDisplayPhoto = otherUser.avatar?.["thumbnail"] && !(otherUser.data.isBlocked || otherUser.data.isBeingBlocked);

                  // Only show the avatar status badge on mobile view (!isDesktopView)
                  let userStatusProp = undefined;
                  if (!isDesktopView) {
                    if (otherUser.data.isBlocked || otherUser.data.isBeingBlocked) {
                      userStatusProp = "dnd";
                    } else if (userIsOnline) {
                      userStatusProp = "available";
                    }
                  }

                  return [
                    <Avatar
                      src={shouldDisplayPhoto ? otherUser.avatar["thumbnail"] : profilePicturePlaceholder}
                      status={userStatusProp}
                    />,
                    otherUser.username, targetIsAdmin, otherUser.data.isBlocked, otherUser.data.isBeingBlocked, userIsOnline, userLastSeen,
                  ];
                }
              }
              return [undefined, undefined, undefined, undefined, undefined, undefined, undefined];
            })();

            return (
              <Conversation
                key={c.id} name={isDesktopView ? name : ""}
                info={
                  !isDesktopView ? "" : isBlocked ? (<span style={{ display: "flex", alignItems: "center", color: "red" }}><Status status="dnd" style={{ marginRight: "5px" }} />You blocked this user!</span>)
                    : isBeingBlocked ? (<span style={{ display: "flex", alignItems: "center", color: "red" }}><Status status="dnd" style={{ marginRight: "5px" }} />You've been blocked!</span>)
                      : isAdmin ? "Admin"
                        : isOnline ? (<span style={{ display: "flex", alignItems: "center" }}><Status status="available" style={{ marginRight: "5px" }} />Online</span>)
                          : lastSeen !== undefined ? `Last seen ${formatLocalTime(lastSeen)}` : ""
                }
                active={activeConversation?.id === c.id} unreadCnt={c.unreadCounter} onClick={() => setActiveConversation(c.id)}
              >
                {avatar}
                <Conversation.Operations visible={isDesktopView}>
                  {isDesktopView && isAdmin && (<Button disabled variant="warning" className="round-button delete-button m-1"><ShieldLock /></Button>)}
                </Conversation.Operations>
              </Conversation>
            );
          })}
        </ConversationList>
      </Sidebar>

      <ChatContainer>
        {activeConversation && (
          <ConversationHeader>
            {activeUserData.avatarElement}
            <ConversationHeader.Content
              userName={
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none fw-bold text-dark"
                    disabled={activeUserData.isBlocked || activeUserData.isBeingBlocked}
                    onClick={() => navigate(`/profile/${activeUserData.id}`)}
                  >
                    {activeUserData.username}
                  </Button>
                  {/* Symbol & Tip visible on both mobile and desktop inside the header space */}
                  {activeUserData.isAdmin && (
                    <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "0.75rem" }}>
                      <ShieldLock size={12} /> Admin
                    </Badge>
                  )}
                </div>
              }
            />
            {/* Action buttons are hidden completely if user is an Admin/Super User */}
            {!activeUserData.isAdmin && (
              <ConversationHeader.Actions>
                <Button variant={activeUserData.isBlocked ? "danger" : "outline-danger"} className="round-button m-1" onClick={() => setShowConfirmation(true)} disabled={activeUserData.isBlocked}><Ban /></Button>
                <Button variant="outline-secondary" className="round-button m-1" onClick={() => handleEmptyMessages(activeUserData.id)}><TrashFill /></Button>
              </ConversationHeader.Actions>
            )}
          </ConversationHeader>
        )}

        <MessageList typingIndicator={getTypingIndicator()}>
          {activeConversation &&
            currentMessages.map((g) => (
              <MessageGroup key={g.id} direction={g.direction}>
                <MessageGroup.Messages>
                  {g.messages.map((m) => (
                    <Message
                      key={m.id} onClick={() => setShowTimestamp(showTimestamp && showTimestamp === m.id ? null : m.id)}
                      model={{ type: "html", payload: m.content, direction: m.direction, position: "normal" }}
                    >
                      <Message.Header sentTime={formatLocalTime(m.createdTime)} />
                    </Message>
                  ))}
                </MessageGroup.Messages>
              </MessageGroup>
            ))}
        </MessageList>

        {activeConversation ? (
          <MessageInput
            value={currentMessage} onChange={handleChange} onSend={handleSend}
            disabled={!activeConversation || activeUserData.isBlocked || activeUserData.isBeingBlocked}
            attachButton={false} placeholder="Type here..."
          />
        ) : null}
      </ChatContainer>
    </MainContainer>
  );
};